import React, { useState, useEffect, useRef } from 'react';
import { X, User, Building2, Loader2 } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { Negociacao } from '../../../../types';
import { validarCPF, validarCNPJ } from '../../../../src/lib/utils';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { propsFundoModal } from '../../../../src/lib/backdropModal';
import { erroPerguntaPagador } from '../../../../src/lib/pagadorUc';
import { aplicarPagadorNaNegociacao } from '../../../../src/services/pagadores';
import { podeEditarCadastro } from '../../../../src/lib/permissaoCadastro';
import PerguntaPagador, { type DadosPerguntaPagador } from '../NewNegotiationWizard/PerguntaPagador';

interface EditClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiation: Negociacao;
    onSave: () => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({ isOpen, onClose, negotiation, onSave }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [loading, setLoading] = useState(false);
    const prevIsOpenRef = useRef(false);

    const [formData, setFormData] = useState({
        cliente_nome: '',
        cpf_cnpj: '',
        email: '',
        whatsapp: '',
        nome_representante_legal: '',
        cpf_representante: '',
        data_nascimento_representante: '',
        tipo_cliente: 'PF' as 'PF' | 'PJ',
        contato_financeiro: '',
        email_financeiro: '',
        whatsapp_financeiro: '',
        contrato_social_data_versao: '',
    });

    // "O assinante também será o pagador?" — o que está gravado e o que está na tela.
    const [pagador, setPagador] = useState<DadosPerguntaPagador>({ pagador_mesmo: null });
    const [pagadorOriginal, setPagadorOriginal] = useState<DadosPerguntaPagador>({ pagador_mesmo: null });

    useEffect(() => {
        if (!isOpen || !negotiation) return;
        let vivo = true;
        const n: any = negotiation;
        (async () => {
            let atual: DadosPerguntaPagador = { pagador_mesmo: n.pagador_e_assinante ?? null };
            const { data } = await supabase
                .from('negociacoes')
                .select('pagador_e_assinante, pagador:pagadores(nome, cpf_cnpj, email, whatsapp)')
                .eq('id', n.id)
                .maybeSingle();
            const p: any = (data as any)?.pagador;
            if (p) {
                atual = { pagador_mesmo: false, pagador_nome: p.nome, pagador_cpf_cnpj: p.cpf_cnpj ?? '', pagador_email: p.email ?? '', pagador_whatsapp: p.whatsapp ?? '' };
            } else if (data) {
                atual = { pagador_mesmo: (data as any).pagador_e_assinante ?? null };
            }
            if (vivo) { setPagador(atual); setPagadorOriginal(atual); }
        })();
        return () => { vivo = false; };
    }, [isOpen, negotiation]);

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!isOpen || !justOpened) return;

        if (isOpen && negotiation) {
            setFormData({
                cliente_nome: negotiation.cliente_nome || '',
                cpf_cnpj: negotiation.cpf_cnpj || '',
                email: negotiation.email || '',
                whatsapp: negotiation.whatsapp || '',
                nome_representante_legal: negotiation.nome_representante_legal || '',
                cpf_representante: negotiation.cpf_representante || '',
                data_nascimento_representante: negotiation.data_nascimento_representante || '',
                tipo_cliente: (negotiation.tipo_cliente as 'PF' | 'PJ') || 'PF',
                contato_financeiro: negotiation.contato_financeiro || '',
                email_financeiro: negotiation.email_financeiro || '',
                whatsapp_financeiro: negotiation.whatsapp_financeiro || '',
                contrato_social_data_versao: negotiation.contrato_social_data_versao || '',
            });
        }
    }, [isOpen, negotiation]);

    const handleTipoChange = (tipo: 'PF' | 'PJ') => {
        setFormData((prev) => {
            if (prev.tipo_cliente === tipo) return prev;
            return {
                ...prev,
                tipo_cliente: tipo,
                cpf_cnpj: '', // CPF e CNPJ têm formatos diferentes; limpa para reentrada
                ...(tipo === 'PF' ? { nome_representante_legal: '', cpf_representante: '', data_nascimento_representante: '', contrato_social_data_versao: '' } : {}),
            };
        });
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.cliente_nome.trim()) {
            addToast(isPJ ? 'A Razão Social é obrigatória.' : 'O nome do cliente é obrigatório.', 'warning');
            return;
        }
        const erroPagador = erroPerguntaPagador(pagador, false);
        if (erroPagador) {
            addToast(erroPagador, 'warning');
            return;
        }

        if (formData.cpf_cnpj) {
            if (!isPJ && !validarCPF(formData.cpf_cnpj)) {
                addToast('CPF inválido.', 'warning');
                return;
            }

            if (isPJ && !validarCNPJ(formData.cpf_cnpj)) {
                addToast('CNPJ inválido.', 'warning');
                return;
            }
        }

        if (isPJ && formData.cpf_representante && !validarCPF(formData.cpf_representante)) {
            addToast('CPF do representante inválido.', 'warning');
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({
                    cliente_nome: formData.cliente_nome,
                    cpf_cnpj: formData.cpf_cnpj,
                    email: formData.email,
                    whatsapp: formData.whatsapp,
                    tipo_cliente: formData.tipo_cliente,
                    nome_representante_legal: isPJ ? formData.nome_representante_legal : null,
                    cpf_representante: isPJ ? formData.cpf_representante : null,
                    data_nascimento_representante: isPJ ? (formData.data_nascimento_representante || null) : null,
                    contato_financeiro: formData.contato_financeiro || null,
                    email_financeiro: formData.email_financeiro || null,
                    whatsapp_financeiro: formData.whatsapp_financeiro || null,
                    contrato_social_data_versao: isPJ ? (formData.contrato_social_data_versao || null) : null,
                })
                .eq('id', negotiation.id);

            if (error) throw error;

            // Pagador do boleto: só quando a resposta (ou o pagador informado) mudou.
            const doc = (v?: string) => (v || '').replace(/\D/g, '');
            const mudouPagador = pagador.pagador_mesmo !== null && pagador.pagador_mesmo !== undefined && (
                pagador.pagador_mesmo !== pagadorOriginal.pagador_mesmo
                || (pagador.pagador_mesmo === false && (
                    doc(pagador.pagador_cpf_cnpj) !== doc(pagadorOriginal.pagador_cpf_cnpj)
                    || (pagador.pagador_email || '') !== (pagadorOriginal.pagador_email || '')
                    || (pagador.pagador_whatsapp || '') !== (pagadorOriginal.pagador_whatsapp || ''))));
            if (mudouPagador) {
                const r = await aplicarPagadorNaNegociacao(
                    negotiation.id,
                    pagador.pagador_mesmo === true,
                    pagador.pagador_mesmo === false
                        ? { nome: pagador.pagador_nome || '', cpf_cnpj: pagador.pagador_cpf_cnpj || '', email: pagador.pagador_email, whatsapp: pagador.pagador_whatsapp }
                        : undefined,
                    { podeAlterarUcEmOperacao: podeEditarCadastro(profile) },
                );
                if (r.ucsPuladas > 0) {
                    addToast(`${r.ucsPuladas} UC(s) desta negociação já estão em operação e continuam com o pagador antigo. Só o Financeiro troca o pagador delas: peça a correção.`, 'warning', 15000);
                }
                if (r.nomeDiferente && r.nomeCadastrado) {
                    addToast(`Este CPF/CNPJ já é do pagador "${r.nomeCadastrado}". O boleto vai sair nesse nome.`, 'warning', 12000);
                }
                addToast(
                    pagador.pagador_mesmo
                        ? `Pagador: o assinante.${r.ucsAtualizadas ? ` ${r.ucsAtualizadas} UC(s) atualizada(s).` : ''}`
                        : `Pagador salvo.${r.ucsAtualizadas ? ` ${r.ucsAtualizadas} UC(s) passaram a usar este pagador.` : ''}`,
                    'success',
                );
                await logNegotiationAction(negotiation.id, user?.id, 'alterou o pagador do boleto', {
                    assinante_paga: pagador.pagador_mesmo,
                    pagador: pagador.pagador_mesmo ? null : { nome: pagador.pagador_nome, cpf_cnpj: doc(pagador.pagador_cpf_cnpj) },
                    ucs_atualizadas: r.ucsAtualizadas,
                });
                setPagadorOriginal(pagador);
            }

            const fieldLabels: Record<string, string> = {
                tipo_cliente: 'Tipo de Cliente',
                cliente_nome: isPJ ? 'Razão Social' : 'Nome',
                cpf_cnpj: isPJ ? 'CNPJ' : 'CPF',
                email: 'E-mail',
                whatsapp: 'WhatsApp',
                nome_representante_legal: 'Representante Legal',
                cpf_representante: 'CPF do Representante',
                data_nascimento_representante: 'Data de Nascimento do Representante',
                contato_financeiro: 'Contato Financeiro',
                email_financeiro: 'E-mail Financeiro',
                whatsapp_financeiro: 'WhatsApp Financeiro',
                contrato_social_data_versao: 'Contrato Social (data/versão)',
            };
            const changes: Record<string, { de: string; para: string }> = {};
            (Object.keys(fieldLabels) as (keyof typeof formData)[]).forEach((key) => {
                const oldVal = String(negotiation[key as keyof Negociacao] || '');
                const newVal = String(formData[key] || '');
                if (oldVal !== newVal) {
                    changes[fieldLabels[key]] = { de: oldVal || '(vazio)', para: newVal || '(vazio)' };
                }
            });
            if (Object.keys(changes).length > 0) {
                await logNegotiationAction(negotiation.id, user?.id, 'editou dados do cliente', { alterações: changes });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'editar_dados_cliente', origem: 'acao' });
            }

            addToast('Dados do cliente atualizados com sucesso!', 'success');
            onSave();
            onClose();
        } catch (error: any) {
            console.error('Erro ao atualizar cliente:', error);
            addToast('Erro ao atualizar dados: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const isPJ = formData.tipo_cliente === 'PJ';

    const maskCPF = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const maskCNPJ = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const maskPhone = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/g, '($1) $2')
            .replace(/(\d)(\d{4})$/, '$1-$2')
            .slice(0, 15);
    };

    return (
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in ${!isOpen ? 'hidden' : ''}`} {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-slide-up border border-gray-100 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                            <User size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Editar Cliente {isPJ ? 'PJ' : 'PF'}</h2>
                            <p className="text-xs text-gray-500 dark:text-slate-300">Atualize os dados cadastrais</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full transition-colors text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-slate-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                            Tipo de Cliente
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => handleTipoChange('PF')}
                                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${!isPJ
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500 hover:border-gray-300 dark:hover:border-slate-600'
                                    }`}
                            >
                                <User size={16} /> Pessoa Física
                            </button>
                            <button
                                type="button"
                                onClick={() => handleTipoChange('PJ')}
                                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${isPJ
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500 hover:border-gray-300 dark:hover:border-slate-600'
                                    }`}
                            >
                                <Building2 size={16} /> Pessoa Jurídica
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                            {isPJ ? 'Razão Social *' : 'Nome do Cliente *'}
                        </label>
                        <input
                            type="text"
                            value={formData.cliente_nome}
                            onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder={isPJ ? "Razão Social" : "Nome completo"}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                            {isPJ ? 'CNPJ' : 'CPF'}
                        </label>
                        <input
                            type="text"
                            value={formData.cpf_cnpj}
                            onChange={(e) => setFormData({ ...formData, cpf_cnpj: isPJ ? maskCNPJ(e.target.value) : maskCPF(e.target.value) })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder={isPJ ? '00.000.000/0000-00' : '000.000.000-00'}
                        />
                    </div>

                    {isPJ && (
                        <>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                                    Nome do Representante Legal
                                </label>
                                <input
                                    type="text"
                                    value={formData.nome_representante_legal}
                                    onChange={(e) => setFormData({ ...formData, nome_representante_legal: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                    placeholder="Nome completo do representante"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                                    CPF do Representante
                                </label>
                                <input
                                    type="text"
                                    value={formData.cpf_representante}
                                    onChange={(e) => setFormData({ ...formData, cpf_representante: maskCPF(e.target.value) })}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                    placeholder="000.000.000-00"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                                    Data de Nascimento do Representante
                                </label>
                                <input
                                    type="date"
                                    value={formData.data_nascimento_representante}
                                    onChange={(e) => setFormData({ ...formData, data_nascimento_representante: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                />
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Pessoa física que assina o termo pela associação (análise de crédito).</p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">
                                    Contrato Social e Alterações (data/versão)
                                </label>
                                <input
                                    type="text"
                                    value={formData.contrato_social_data_versao}
                                    onChange={(e) => setFormData({ ...formData, contrato_social_data_versao: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                    placeholder="Ex: 3ª alteração, de 12/05/2024"
                                />
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Documento societário base citado no termo de adesão.</p>
                            </div>
                        </>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">E-mail</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="email@exemplo.com"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">WhatsApp</label>
                        <input
                            type="text"
                            value={formData.whatsapp}
                            onChange={(e) => setFormData({ ...formData, whatsapp: maskPhone(e.target.value) })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="(00) 00000-0000"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">Contato Financeiro</label>
                            <input
                                type="text"
                                value={formData.contato_financeiro}
                                onChange={(e) => setFormData({ ...formData, contato_financeiro: e.target.value })}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                placeholder="Nome do responsável financeiro"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">E-mail Financeiro</label>
                            <input
                                type="email"
                                value={formData.email_financeiro}
                                onChange={(e) => setFormData({ ...formData, email_financeiro: e.target.value })}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                placeholder="financeiro@exemplo.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">WhatsApp Financeiro</label>
                            <input
                                type="text"
                                value={formData.whatsapp_financeiro}
                                onChange={(e) => setFormData({ ...formData, whatsapp_financeiro: maskPhone(e.target.value) })}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                                placeholder="(00) 00000-0000"
                            />
                        </div>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-slate-500 -mt-2">Aparecem no Quadro Resumo do termo de adesão. Cada campo vazio sai com o dado principal do cliente (nome, e-mail ou WhatsApp).</p>

                    <PerguntaPagador
                        dados={pagador}
                        obrigatoria={false}
                        sugestao={{ email: formData.email_financeiro, whatsapp: formData.whatsapp_financeiro }}
                        onChange={(campo, valor) => setPagador(prev => ({ ...prev, [campo]: valor }))}
                    />
                    {pagadorOriginal.pagador_mesmo === null && pagador.pagador_mesmo === null ? (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 -mt-2">Ainda não informado nesta negociação: o boleto segue o pagador de cada UC.</p>
                    ) : null}

                    <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 dark:border-slate-800 mt-6 pb-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-white bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-colors"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            Salvar Alterações
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditClientModal;
